import React from "react";
import {
  LocationOn,
  ModeEditOutlineOutlined,
  Tag,
  Abc,
  LiveHelp,
  MonitorWeight,
  Height,
  Straighten,
  WidthFull,
  Thermostat,
  LocalShipping,
  Style,
  GroupWork,
  Speed,
  Delete,
  LocalGasStation,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Link,
  List,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import { useState } from "react";
import {
  GetTrailerDetailsQuery,
  TrailerEvent,
  TrailerEventsQuery,
  TripAssetTypes,
  useGetGoodProfileListQuery,
} from "../../../graphql/generated";
import toLatLng from "../../../utils/geo/toLaLng";
import formatWeight from "../../../utils/labels/formatWeight";
import formatDistance, {
  formatMileage,
} from "../../../utils/labels/formatDistance";
import InfoBlock from "../../common/InfoBlock";
import LabeledInfo from "../../common/LabeledInfo";
import Map, { createMarker } from "../../common/Map/Map";
import formatTemperature from "../../../utils/labels/formatTemperature";
import { hasValue } from "../../../utils/form/hasValue";

import trailerTypeLabel from "../../../utils/labels/trailerTypeLabel";
import FeatureGuard, { Feature } from "../../account/Access/FeatureGuard";
import { useTranslation } from "react-i18next";
import ChipTagsList from "../../common/ChipTagsList/ChipTagsList";
import MaintenanceTasksListContainer from "../../maintenance/MaintenanceTasksList";
import GroupsDisplayContainer from "../GroupsDisplay";
import CustomFieldsDisplayContainer from "../../custom-field-definitions/CustomFieldsDisplay";
import RoleGuard from "../../account/Access/RoleGuard";
import AssetLinkingsListContainer from "../AssetLinkingsList";
import EventReport from "../../tracking/EventReport";
import { EventReportType } from "../../tracking/EventReport/EventReport";
import EmptyState from "../../common/EmptyState";
import TrailerEventFormModalContainer from "../TrailerEventFormModal";
import { testIds } from "./TrailerDetails.testIds";
import CompartmentGraphic, {
  CompartmentCellData,
} from "../../common/CompartmentGraphic/CompartmentGraphic";
import { useOrgSettings } from "../../account/Access/OrgSettingsGuard";
import CompartmentVolumeEditModal from "./CompartmentVolumeEditModal";

export type TrailerDetailsData = GetTrailerDetailsQuery["trailerById"];

type TrailerDetailsProps = {
  trailer: TrailerDetailsData;
  onEdit: () => void;
  onDelete: () => void;
  trailerEvents?: TrailerEventsQuery["trailerEvents"]["data"];
};

function TrailerDetails({
  trailer,
  onEdit,
  onDelete,
  trailerEvents,
}: TrailerDetailsProps) {
  const { t } = useTranslation(["assets", "common", "orders"]);
  const [isAddressMapOpen, setIsAddressMapOpen] = useState(false);
  const [isTrailerEventFormOpen, setIsTrailerEventFormOpen] = useState(false);
  const [focusedTrailerEvent, setFocusedTrailerEvent] =
    useState<TrailerEvent | null>(null);
  const [editVolumeCompartmentId, setEditVolumeCompartmentId] = useState<
    string | null
  >(null);

  const orgSettings = useOrgSettings();
  const trackCommodity = orgSettings?.dispatching?.trackCommodity === true;

  // Build a commodityId → label map for the volume layer
  const goodProfilesQuery = useGetGoodProfileListQuery(undefined, {
    enabled: trackCommodity,
  });
  const goodProfilesMap: Record<string, string> = {};
  for (const gp of goodProfilesQuery.data?.goodProfiles?.data ?? []) {
    goodProfilesMap[gp._id] = gp.label;
  }

  // Map trailer compartments to CompartmentCellData for the graphic
  const compartmentCells: CompartmentCellData[] = (
    trailer.compartments || []
  ).map((c) => ({
    identifier: c.identifier,
    capacity: c.capacity,
    unit: c.unit,
    hasDoubleBulkheadAfter: c.hasDoubleBulkheadAfter,
    maxVolume: c.maxVolume,
    trailerId: trailer._id,
  }));

  return (
    <Box>
      <Stack direction="row" flexDirection="row-reverse" spacing={2}>
        <RoleGuard roles={["Carrier Admin"]}>
          <IconButton data-testid={testIds.trailerDetailsDelete} onClick={onDelete} color="error">
            <Delete />
          </IconButton>
        </RoleGuard>
        <IconButton data-testid={testIds.trailerDetailsEdit} onClick={onEdit} color="primary">
          <ModeEditOutlineOutlined />
        </IconButton>
      </Stack>
      <Grid container spacing={3}>
        <Grid item sm={6}>
          <InfoBlock title={t("assets:trailer.details", "Trailer Details")}>
            <LabeledInfo
              title={t("assets:code", "Asset Code")}
              value={trailer.serialNumber}
              icon={<Abc />}
            />
            <LabeledInfo
              title={t("assets:licenseNumber", "License Number")}
              value={trailer.licenseNumber?.toString()}
              icon={<Tag />}
            />
            <LabeledInfo
              title={t("assets:vin", "VIN#")}
              value={trailer.vin?.toString()}
              icon={<Tag />}
            />
            <LabeledInfo
              title={t("assets:serial", "Serial Number")}
              value={trailer.serial?.toString()}
              icon={<Tag />}
            />
            <LabeledInfo
              title={t("assets:trailer.type", "Type")}
              value={trailerTypeLabel(trailer.type)}
              icon={<LocalShipping />}
            />
            <FeatureGuard feature={Feature.CommodityManagement}>
              <LabeledInfo
                title={t("assets:trailer.hasMeteredPump", "Has Metered Pump")}
                value={trailer.hasMeteredPump ? t("common:yes", "Yes") : t("common:no", "No")}
                icon={<LocalGasStation />}
              />
            </FeatureGuard>
            <LabeledInfo
              title="Mileage"
              value={trailer.mileage ? formatMileage(trailer.mileage) : "N/A"}
              icon={<Speed />}
            />
            <LabeledInfo
              title={t("assets:status", "Status")}
              value={trailer.status}
              icon={<LiveHelp />}
            />

            <LabeledInfo
              title={t("common:tags")}
              value={<ChipTagsList value={trailer.tags || []} />}
              icon={<Style />}
            />

            <LabeledInfo
              title={t("common:groups")}
              value={<GroupsDisplayContainer groupIds={trailer.groupIds} />}
              icon={<GroupWork />}
            />
          </InfoBlock>
        </Grid>

        <Grid item sm={6}>
          <InfoBlock title={t("assets:domicile", "Domicile")}>
            <LabeledInfo data-testid="trailer-details-open-domicile-map"
              title={t("assets:address", "Address")}
              value={trailer.domicile?.label}
              icon={<LocationOn />}
              onClick={() => {
                if (trailer.domicile) {
                  setIsAddressMapOpen(true);
                }
              }}
            />
          </InfoBlock>
        </Grid>

        <Grid item sm={6}>
          <InfoBlock title={t("assets:trailer.capacity", "Trailer Capacity")}>
            <LabeledInfo
              title={t("assets:trailer.width", "Width")}
              value={
                hasValue(trailer.width) ? formatDistance(trailer.width) : "N/A"
              }
              icon={<WidthFull />}
            />
            <LabeledInfo
              title={t("assets:trailer.height", "Height")}
              value={
                hasValue(trailer.height)
                  ? formatDistance(trailer.height)
                  : "N/A"
              }
              icon={<Height />}
            />
            <LabeledInfo
              title={t("assets:trailer.length", "Length")}
              value={
                hasValue(trailer.length)
                  ? formatDistance(trailer.length)
                  : "N/A"
              }
              icon={<Straighten />}
            />
            <LabeledInfo
              title={t("assets:trailer.numberOfAxles", "Number of Axles")}
              value={
                hasValue(trailer.numberOfAxles)
                  ? trailer.numberOfAxles?.toString()
                  : "N/A"
              }
              icon={<Straighten />}
            />
            <LabeledInfo
              title={t("assets:trailer.maxWeight", "Max Weight")}
              value={
                hasValue(trailer.maxWeight)
                  ? formatWeight(Number(trailer.maxWeight))
                  : "N/A"
              }
              icon={<MonitorWeight />}
            />
            <LabeledInfo
              title={t("assets:trailer.minTemperature", "Min Temperature")}
              value={
                hasValue(trailer.minTemperature)
                  ? formatTemperature(trailer.minTemperature)
                  : "N/A"
              }
              icon={<Thermostat />}
            />
            <LabeledInfo
              title={t("assets:trailer.maxTemperature", "Max Temperature")}
              value={
                hasValue(trailer.maxTemperature)
                  ? formatTemperature(trailer.maxTemperature)
                  : "N/A"
              }
              icon={<Thermostat />}
            />
          </InfoBlock>
        </Grid>
        <Grid item sm={12}>
          <InfoBlock title={t("assets:trailer.compartments", "Compartments")}>
            <CompartmentGraphic
              compartments={compartmentCells}
              compartmentVolumes={
                trackCommodity
                  ? (trailer.compartmentVolumes ?? []).map((v) => ({
                      _id: v._id,
                      compartmentId: v.compartmentId,
                      trailerId: trailer._id,
                      commodityId: v.commodityId ?? undefined,
                      quantity: v.quantity,
                      lastUpdated: v.lastUpdated,
                      lastShipmentId: v.lastShipmentId ?? null,
                      lastShipmentNumber: v.lastShipmentNumber ?? null,
                      updatedBy: v.updatedBy,
                    }))
                  : undefined
              }
              goodProfilesMap={trackCommodity ? goodProfilesMap : undefined}
              onEditVolume={
                trackCommodity
                  ? (compartmentId) => {
                      setEditVolumeCompartmentId(compartmentId);
                    }
                  : undefined
              }
            />
            <FeatureGuard feature={Feature.CommodityManagement}>
              {(trailer.compartments ?? []).length > 0 && (
                <Table sx={{ mt: 2 }} size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t("assets:trailer.identifier", "Identifier")}</TableCell>
                      <TableCell>{t("assets:trailer.compartmentCapacity", "Capacity")}</TableCell>
                      <TableCell>{t("assets:commodity.unit", "Unit")}</TableCell>
                      <TableCell>{t("assets:trailer.minVolume", "Min Volume")}</TableCell>
                      <TableCell>{t("assets:trailer.maxVolume", "Max Volume")}</TableCell>
                      <TableCell>{t("assets:trailer.heavyOnly", "Heavy Only")}</TableCell>
                      <TableCell>{t("assets:trailer.doubleBulkhead", "Double Bulkhead")}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(trailer.compartments ?? []).map((compartment, index) => (
                      <TableRow key={index}>
                        <TableCell>{compartment.identifier}</TableCell>
                        <TableCell>{compartment.capacity}</TableCell>
                        <TableCell>{compartment.unit}</TableCell>
                        <TableCell>{compartment.minVolume ?? "—"}</TableCell>
                        <TableCell>{compartment.maxVolume ?? "—"}</TableCell>
                        <TableCell>{compartment.heavyOnly ? t("common:yes", "Yes") : t("common:no", "No")}</TableCell>
                        <TableCell>{compartment.hasDoubleBulkheadAfter ? t("common:yes", "Yes") : t("common:no", "No")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </FeatureGuard>
          </InfoBlock>
        </Grid>

        <Grid item xs={12}>
          <CustomFieldsDisplayContainer customFields={trailer.customFields} />
        </Grid>

        <Grid item xs={12}>
          <InfoBlock title="Asset Linking">
            <AssetLinkingsListContainer
              assetType={TripAssetTypes.Trailer}
              assetId={trailer._id}
            />
          </InfoBlock>
        </Grid>

        <Grid item xs={12}>
          <InfoBlock title="Maintenance Tasks">
            <MaintenanceTasksListContainer
              assetType={TripAssetTypes.Trailer}
              assetId={trailer._id}
            />
          </InfoBlock>
        </Grid>

        <Grid item xs={12}>
          <InfoBlock
            title={t("orders:events", "Events")}
            action={
              <Button data-testid={testIds.trailerDetailsAddEvent}
                variant="contained"
                color="primary"
                onClick={() => {
                  setIsTrailerEventFormOpen(true);
                }}
                sx={{ mt: 2 }}
              >
                {t("common:add", "Add")}
              </Button>
            }
          >
            <Box maxHeight={300} overflow="auto">
              {trailerEvents && trailerEvents.length > 0 ? (
                <List sx={{ pt: 0 }}>
                  {trailerEvents.map((trailerEvent) => {
                    return (
                      <React.Fragment key={trailerEvent._id}>
                        <EventReport
                          eventReport={{
                            _id: trailerEvent._id,
                            type: EventReportType.INFO,
                            date: trailerEvent.date,
                            message: "The trailer has been washed.",
                            shipment: {
                              _id: "",
                              shipmentNumber: "",
                            },
                            trip: null,
                            title: "Trailer washed",
                          }}
                          onClick={() => {
                            setFocusedTrailerEvent(trailerEvent);
                            setIsTrailerEventFormOpen(true);
                          }}
                          showShipmentLink={false}
                        />
                      </React.Fragment>
                    );
                  })}
                </List>
              ) : (
                <EmptyState />
              )}
            </Box>
            <TrailerEventFormModalContainer
              isOpen={isTrailerEventFormOpen}
              onClose={async () => {
                setIsTrailerEventFormOpen(false);
                setFocusedTrailerEvent(null);
              }}
              trailerEvent={focusedTrailerEvent}
              trailerId={trailer._id}
            />
          </InfoBlock>
        </Grid>

        <Grid item sm={12}>
          <InfoBlock title={t("common:documents.many")}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{t("common:name")}</TableCell>
                  <TableCell
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    {t("common:documents.one")}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {trailer.documents?.map((document) => (
                  <TableRow key={document._id}>
                    <TableCell>{document.name}</TableCell>
                    <TableCell>
                      <Link data-testid={`${testIds.trailerDetailsOpen}-${document._id}`} href={document.url} component="a" target="_blank">
                        Open
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </InfoBlock>
        </Grid>
      </Grid>

      {trailer.domicile && (
        <Dialog
          open={isAddressMapOpen}
          onClose={() => setIsAddressMapOpen(false)}
        >
          <DialogTitle>{trailer.domicile?.label}</DialogTitle>

          <DialogContent>
            <Map
              center={toLatLng(trailer?.domicile?.coordinates)}
              zoom={13}
              markers={
                trailer?.domicile?.coordinates
                  ? [createMarker(toLatLng(trailer.domicile.coordinates))]
                  : []
              }
            />
          </DialogContent>

          <DialogActions>
            <Button data-testid={testIds.trailerDetailsClose} onClick={() => setIsAddressMapOpen(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      )}

      {/* Compartment Volume Edit Modal */}
      {trackCommodity && (() => {
        const editCompartment = editVolumeCompartmentId
          ? compartmentCells.find((c) => c.identifier === editVolumeCompartmentId) ?? null
          : null;
        const editVolume = editVolumeCompartmentId
          ? (trailer.compartmentVolumes ?? []).find(
              (v) => v.compartmentId === editVolumeCompartmentId
            )
          : null;
        return (
          <CompartmentVolumeEditModal
            open={!!editVolumeCompartmentId}
            onClose={() => setEditVolumeCompartmentId(null)}
            trailerId={trailer._id}
            compartment={editCompartment}
            currentCommodityId={editVolume?.commodityId ?? null}
            currentQuantity={editVolume?.quantity ?? null}
          />
        );
      })()}
    </Box>
  );
}

export default TrailerDetails;
